<script lang="ts">
	import { ArtistsAboutId } from '../../constants';
	import { ArtistUpdatesCard } from '../ArtistUpdatesCard';
	import { Anchor } from '#lib/features/drop-page/components/DropAnchor/Anchor/index.js';
	import { page } from '$app/state';
	import { AccordionItem, Accordion } from '$global/components/Accordion';
	import { Markdown } from '$global/components/Markdown';
	import { Tabs } from '$global/components/Tabs';
	import { Txt } from '$global/components/Txt';
	import { getImageUrl } from '$global/utils/getImageUrl';
	import type { ArtistsPageData } from '$routes/artists/[slug]/types';

	interface Props {
		dataCy: string;
		class?: string;
	}

	let { dataCy, ...rest }: Props = $props();

	let dataCyPrefix = $derived(`${dataCy}-artists-about`);
	let artist = $derived(page.data.artist as ArtistsPageData['artist']);

	let args = $derived({
		dataCy: dataCyPrefix,
		tabs: [
			{
				id: '1',
				title: 'Biography',
			},
			...(artist?.bio_series?.length
				? [
						{
							id: '2',
							title: 'Key themes',
						},
					]
				: []),
		],
	});

	let activeTab = $state(0);
</script>

<div class={rest.class}>
	<Anchor
		hasTabs={false}
		id={`#${ArtistsAboutId}`}
		options={{ behavior: 'smooth' }}
		delay={1000}
	/>
	<Txt class="my-8" variant="h3">About the Artist</Txt>

	<div class="flex flex-col sm:flex-row">
		<Tabs
			bind:activeTab
			class="flex flex-1 flex-col"
			classes={{
				button: 'border-none',
				/*
					`button: border-none` drops the bottom border that the active-tab
					bar is nudged onto, so a 1px bar landed in the gap and rendered
					only at some zoom levels / device pixel ratios. Give it its own
					height and drop the nudge so it no longer depends on that border.
				*/
				currentTabBorder: 'h-[2px] translate-y-0',
				container: !activeTab ? 'sm:mt-9 sm:mr-8 lg:mr-20' : '',
			}}
			{...args}
		>
			{#snippet tabsHeader()}
				<div class="border-b border-gray-200"></div>
			{/snippet}

			{#if activeTab === 0 && artist?.bio_long}
				<Markdown dataCy={`${dataCyPrefix}-tabs`} source={artist.bio_long} />
			{/if}

			{#if artist.bio_series && activeTab === 1}
				<Accordion multiple>
					{#each artist.bio_series as series, i}
						{#if i > 0}
							<div class="border-b border-gray-200"></div>
						{/if}
						<AccordionItem
							dataCy={`${dataCyPrefix}-details`}
							defaultOpen={i === 0}
						>
							{#snippet titleSlot()}
								<div class="flex w-full items-center py-4">
									{#if series?.images}
										<img
											src={getImageUrl(
												series.images[0]?.image?.filename_disk,
												'512px'
											)}
											alt={series?.title || ''}
											class="mr-4 h-[80px] w-[80px] object-cover"
										/>
									{/if}
									<Txt variant="h5" class="py-4 text-left">
										{series?.title || ''}</Txt
									>
								</div>
							{/snippet}

							<div class="flex sm:flex-row">
								{#if series?.long_text}
									<div class="mt-4 mr-8 mb-12 flex flex-1">
										<Markdown
											size="medium"
											{dataCy}
											source={series?.long_text}
										/>
									</div>
								{/if}

								<div
									class="my-4 hidden sm:flex sm:h-[300px] sm:w-[300px] lg:h-[400px] lg:w-[400px]"
								>
									<img
										class="h-full w-full object-cover"
										src={getImageUrl(
											artist?.bio_series?.[i]?.images?.[0]?.image
												?.filename_disk,
											'2048px'
										)}
										alt={artist?.name || ''}
									/>
								</div>
							</div>
						</AccordionItem>
					{/each}
				</Accordion>
			{/if}
		</Tabs>
		{#if activeTab === 0}
			<div class="w-[full] sm:w-[320px]">
				<div class="border-gray-200 sm:mt-[53px] sm:mb-10 sm:border-b"></div>
				<ArtistUpdatesCard
					dataCy={dataCyPrefix}
					src={artist?.image?.filename_disk}
					artistName={artist?.name || ''}
					listId={artist?.news_letter_list_id_prod || 0}
					testListId={artist?.news_letter_list_id_dev || 0}
				/>
			</div>
		{/if}
	</div>
</div>
